import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SaveIcon from '@mui/icons-material/Save';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { pageColors } from './HomePage';
import { changePassword, fetchProfile, updateProfile } from '../api/profile';

function formatRole(role) {
  return role ? role.replace(/_/g, ' ') : 'Authenticated';
}

function formatDate(value) {
  if (!value) {
    return 'N/A';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString();
}

function createProfileDraft(profile) {
  return {
    firstName: profile?.firstName || '',
    lastName: profile?.lastName || '',
    email: profile?.email || '',
    phone: profile?.phone || '',
  };
}

function createPasswordDraft() {
  return {
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  };
}

export default function ProfilePage({ currentUser, onRefreshSession }) {
  const [profile, setProfile] = useState(null);
  const [profileDraft, setProfileDraft] = useState(createProfileDraft(null));
  const [passwordDraft, setPasswordDraft] = useState(createPasswordDraft);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [profileFeedback, setProfileFeedback] = useState(null);
  const [passwordFeedback, setPasswordFeedback] = useState(null);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordVisibility, setPasswordVisibility] = useState({
    currentPassword: false,
    newPassword: false,
    confirmNewPassword: false,
  });

  const loadProfile = useCallback(async () => {
    setIsLoadingProfile(true);
    setLoadError('');

    try {
      const nextProfile = await fetchProfile();
      setProfile(nextProfile);
      setProfileDraft(createProfileDraft(nextProfile));
      return nextProfile;
    } catch (error) {
      setLoadError(error.message || 'Failed to load profile.');
      throw error;
    } finally {
      setIsLoadingProfile(false);
    }
  }, []);

  useEffect(() => {
    loadProfile().catch((error) => {
      console.error('Failed to load profile:', error);
    });
  }, [loadProfile]);

  const handleProfileSubmit = async (event) => {
    event.preventDefault();
    setProfileFeedback(null);
    setIsSavingProfile(true);

    try {
      const updatedProfile = await updateProfile({
        firstName: profileDraft.firstName.trim(),
        lastName: profileDraft.lastName.trim(),
        email: profileDraft.email.trim(),
        phone: profileDraft.phone.trim(),
      });

      setProfile(updatedProfile);
      setProfileDraft(createProfileDraft(updatedProfile));
      await onRefreshSession();
      setProfileFeedback({ severity: 'success', message: 'Profile updated successfully.' });
    } catch (error) {
      setProfileFeedback({ severity: 'error', message: error.message || 'Failed to update profile.' });
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setPasswordFeedback(null);

    const trimmedCurrentPassword = passwordDraft.currentPassword.trim();
    const trimmedNewPassword = passwordDraft.newPassword.trim();
    const trimmedConfirmPassword = passwordDraft.confirmNewPassword.trim();

    if (!trimmedCurrentPassword || !trimmedNewPassword || !trimmedConfirmPassword) {
      setPasswordFeedback({ severity: 'error', message: 'Please complete all password fields.' });
      return;
    }

    if (trimmedNewPassword !== trimmedConfirmPassword) {
      setPasswordFeedback({ severity: 'error', message: 'New password confirmation does not match.' });
      return;
    }

    setIsChangingPassword(true);

    try {
      await changePassword({
        currentPassword: trimmedCurrentPassword,
        newPassword: trimmedNewPassword,
      });
      setPasswordDraft(createPasswordDraft());
      setPasswordFeedback({ severity: 'success', message: 'Password changed successfully.' });
    } catch (error) {
      setPasswordFeedback({ severity: 'error', message: error.message || 'Failed to change password.' });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const displayName =
    profile?.displayName || currentUser?.displayName || currentUser?.username || 'Authenticated user';
  const profileLoaded = Boolean(profile);
  const isActive = profile?.active ?? currentUser?.active;
  const profileColumnsSx = {
    xs: 'minmax(0, 1fr)',
    md: 'minmax(0, 7fr) minmax(0, 5fr)',
  };
  const responsiveChipSx = {
    maxWidth: '100%',
    height: 'auto',
    '& .MuiChip-label': {
      display: 'block',
      whiteSpace: 'normal',
      overflowWrap: 'anywhere',
      py: 0.75,
    },
  };

  const togglePasswordVisibility = (field) => {
    setPasswordVisibility((currentVisibility) => ({
      ...currentVisibility,
      [field]: !currentVisibility[field],
    }));
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'radial-gradient(circle at top left, rgba(30,136,229,0.16), transparent 55%), #f7f9fb',
        py: { xs: 4, md: 6 },
        px: { xs: 2, sm: 3 },
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <Paper
            elevation={0}
            sx={{
              py: { xs: 2.5, md: 3 },
              borderRadius: 4,
              backgroundColor: '#fff',
              boxShadow: '0 18px 40px rgba(0,0,0,0.07)',
            }}
          >
            <Box
              sx={{
                px: { xs: 2.5, md: 3 },
                display: 'grid',
                gridTemplateColumns: profileColumnsSx,
                gap: 3,
                alignItems: 'flex-start',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                    <Avatar
                      sx={{
                        width: 72,
                        height: 72,
                        bgcolor: `${pageColors.connect}18`,
                        color: pageColors.connect,
                      }}
                    >
                      {displayName.charAt(0).toUpperCase()}
                    </Avatar>

                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="overline" sx={{ color: pageColors.connect, letterSpacing: 3 }}>
                        MY ACCOUNT
                      </Typography>
                      <Typography variant="h4" fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>
                        {displayName}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                        @{profile?.username || currentUser?.username || 'user'}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Stack spacing={1.25} alignItems="flex-start">
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ maxWidth: '100%' }}>
                    <Chip
                      label={formatRole(profile?.role || currentUser?.role)}
                      color="primary"
                      variant="outlined"
                      sx={responsiveChipSx}
                    />
                    <Chip
                      label={profile?.company || currentUser?.company || '__system__'}
                      variant="outlined"
                      sx={responsiveChipSx}
                    />
                    <Chip
                      label={isActive ? 'Active' : 'Inactive'}
                      color={isActive ? 'success' : 'default'}
                      sx={responsiveChipSx}
                    />
                  </Stack>
                </Stack>
              </Box>
            </Box>
          </Paper>

          {isLoadingProfile && <LinearProgress />}
          {loadError && <Alert severity="error">{loadError}</Alert>}

          {!profileLoaded && !isLoadingProfile ? (
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 4,
                backgroundColor: '#fff',
                boxShadow: '0 18px 40px rgba(0,0,0,0.07)',
              }}
            >
              <Stack spacing={2}>
                <Typography variant="h6" fontWeight={700}>
                  Profile unavailable
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  The profile could not be loaded from the backend. Retry before editing account details.
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => loadProfile().catch(() => {})}
                  sx={{ alignSelf: 'flex-start', borderRadius: '999px', textTransform: 'none' }}
                >
                  Retry
                </Button>
              </Stack>
            </Paper>
          ) : (
            <Stack spacing={3}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: profileColumnsSx,
                  gap: 3,
                  alignItems: 'stretch',
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 2.5, md: 3 },
                      borderRadius: 4,
                      backgroundColor: '#fff',
                      boxShadow: '0 18px 40px rgba(0,0,0,0.07)',
                      height: '100%',
                    }}
                  >
                    <Stack spacing={3} component="form" onSubmit={handleProfileSubmit}>
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Avatar sx={{ bgcolor: `${pageColors.connect}18`, color: pageColors.connect, width: 40, height: 40 }}>
                          <PersonOutlineIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" fontWeight={700}>
                            Personal information
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Update the profile details used across the shell.
                          </Typography>
                        </Box>
                      </Stack>

                      {profileFeedback && <Alert severity={profileFeedback.severity}>{profileFeedback.message}</Alert>}

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                          label="First name"
                          value={profileDraft.firstName}
                          onChange={(event) =>
                            setProfileDraft((currentDraft) => ({ ...currentDraft, firstName: event.target.value }))
                          }
                          fullWidth
                          required
                        />
                        <TextField
                          label="Last name"
                          value={profileDraft.lastName}
                          onChange={(event) =>
                            setProfileDraft((currentDraft) => ({ ...currentDraft, lastName: event.target.value }))
                          }
                          fullWidth
                        />
                      </Stack>

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                          label="Email"
                          type="email"
                          value={profileDraft.email}
                          onChange={(event) =>
                            setProfileDraft((currentDraft) => ({ ...currentDraft, email: event.target.value }))
                          }
                          fullWidth
                          required
                        />
                        <TextField
                          label="Phone"
                          value={profileDraft.phone}
                          onChange={(event) =>
                            setProfileDraft((currentDraft) => ({ ...currentDraft, phone: event.target.value }))
                          }
                          fullWidth
                        />
                      </Stack>

                      <Box
                        aria-hidden="true"
                        sx={{
                          display: { xs: 'none', md: 'block' },
                          minHeight: 56,
                          visibility: 'hidden',
                          pointerEvents: 'none',
                        }}
                      />

                      <Button
                        type="submit"
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={isSavingProfile || isLoadingProfile}
                        sx={{ alignSelf: 'flex-start', borderRadius: '999px', textTransform: 'none' }}
                      >
                        {isSavingProfile ? 'Saving...' : 'Save profile'}
                      </Button>
                    </Stack>
                  </Paper>
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 2.5, md: 3 },
                      borderRadius: 4,
                      backgroundColor: '#fff',
                      boxShadow: '0 18px 40px rgba(0,0,0,0.07)',
                      height: '100%',
                    }}
                  >
                    <Stack spacing={3} component="form" onSubmit={handlePasswordSubmit}>
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Avatar sx={{ bgcolor: `${pageColors.users}18`, color: pageColors.users, width: 40, height: 40 }}>
                          <LockResetIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" fontWeight={700}>
                            Change password
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Confirm your current password before setting a new one.
                          </Typography>
                        </Box>
                      </Stack>

                      {passwordFeedback && <Alert severity={passwordFeedback.severity}>{passwordFeedback.message}</Alert>}

                      <TextField
                        label="Current password"
                        type={passwordVisibility.currentPassword ? 'text' : 'password'}
                        value={passwordDraft.currentPassword}
                        onChange={(event) =>
                          setPasswordDraft((currentDraft) => ({
                            ...currentDraft,
                            currentPassword: event.target.value,
                          }))
                        }
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                edge="end"
                                aria-label="Toggle current password visibility"
                                onClick={() => togglePasswordVisibility('currentPassword')}
                              >
                                {passwordVisibility.currentPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        fullWidth
                      />

                      <TextField
                        label="New password"
                        type={passwordVisibility.newPassword ? 'text' : 'password'}
                        value={passwordDraft.newPassword}
                        onChange={(event) =>
                          setPasswordDraft((currentDraft) => ({
                            ...currentDraft,
                            newPassword: event.target.value,
                          }))
                        }
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                edge="end"
                                aria-label="Toggle new password visibility"
                                onClick={() => togglePasswordVisibility('newPassword')}
                              >
                                {passwordVisibility.newPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        fullWidth
                      />

                      <TextField
                        label="Confirm new password"
                        type={passwordVisibility.confirmNewPassword ? 'text' : 'password'}
                        value={passwordDraft.confirmNewPassword}
                        onChange={(event) =>
                          setPasswordDraft((currentDraft) => ({
                            ...currentDraft,
                            confirmNewPassword: event.target.value,
                          }))
                        }
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                edge="end"
                                aria-label="Toggle confirm new password visibility"
                                onClick={() => togglePasswordVisibility('confirmNewPassword')}
                              >
                                {passwordVisibility.confirmNewPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        fullWidth
                      />

                      <Button
                        type="submit"
                        variant="contained"
                        disabled={isChangingPassword || isLoadingProfile}
                        sx={{ alignSelf: 'flex-start', borderRadius: '999px', textTransform: 'none' }}
                      >
                        {isChangingPassword ? 'Updating...' : 'Update password'}
                      </Button>
                    </Stack>
                  </Paper>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: profileColumnsSx,
                  gap: 3,
                  alignItems: 'start',
                }}
              >
                <Box sx={{ display: { xs: 'none', md: 'block' } }} />
                <Box sx={{ minWidth: 0 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      mt: { xs: 0, md: 4 },
                      p: { xs: 2.5, md: 3 },
                      borderRadius: 4,
                      backgroundColor: '#fff',
                      boxShadow: '0 18px 40px rgba(0,0,0,0.07)',
                    }}
                  >
                    <Stack spacing={2}>
                      <Typography variant="h6" fontWeight={700}>
                        Account metadata
                      </Typography>

                      <Stack spacing={1.5}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Username
                          </Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
                            {profile?.username || currentUser?.username || 'N/A'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Role
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {formatRole(profile?.role || currentUser?.role)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Company
                          </Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
                            {profile?.company || currentUser?.company || '__system__'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Created
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {formatDate(profile?.createdAt)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Updated
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {formatDate(profile?.updatedAt)}
                          </Typography>
                        </Box>
                      </Stack>
                    </Stack>
                  </Paper>
                </Box>
              </Box>
            </Stack>
          )}
        </Stack>
      </Container>
    </Box>
  );
}
